import React from "react";

import {
  BULK_UPSERT_STUDENT_REAL_PHOTOS_FAILURE_MESSAGE,
  BULK_UPSERT_STUDENT_REAL_PHOTOS_SUCCESS_MESSAGE,
  bulkUpsertStudentRealPhotos,
  CLEAR_STUDENT_REAL_PHOTOS,
  clearErrorMessage,
  clearStudents,
  clearSuccessMessage,
  Context,
  DELETE_STUDENT_REAL_PHOTO_UPLOADED_FILE_FAILURE_MESSAGE,
  FETCH_STUDENTS_SUCCESS_MESSAGE,
  fetchClasses,
  fetchStudentRealPhotoUploadedFiles,
  fetchStudents,
  UPLOAD_STUDENT_REAL_PHOTOS_FAILURE_MESSAGE,
  uploadStudentRealPhotos,
  deleteStudentRealPhotoUploadedFile,
} from "../../../managerStore";
import { BulkUpsertStudentRealPhotosRequest } from "../../../state";
import useCurrentYear from "../../../hooks/manager/useCurrentYear";
import useWaitApiCall from "../../../hooks/useWaitApiCall";
import Toast from "../../../molecules/Toast";
import UploadStudentRealPhotos from "../../../templates/manager/UploadStudentRealPhotos";
import { StudentWithUploadedFile } from "../../../templates/manager/UploadStudentRealPhotos/StudentListItem";

type DeletedRealPhotoId = {
  student_id: number;
  real_photo_id: number;
};

export default function UploadStudentRealPhotosPage() {
  const { state, dispatch } = React.useContext(Context);
  const [calling, peep] = useWaitApiCall(state);
  const { currentYear } = useCurrentYear();

  const [selectedClassId, setSelectedClassId] = React.useState<number>(0);
  const [studentsWithPhoto, setStudentsWithPhoto] = React.useState<
    StudentWithUploadedFile[]
  >([]);
  const [deletedRealPhotoIds, setDeletedRealPhotoIds] = React.useState<
    DeletedRealPhotoId[]
  >([]);

  const assignedUploadedPhotoIds = React.useMemo(() => {
    const ids = studentsWithPhoto
      .map(student => student.uploadedFile?.id)
      .filter((id): id is number => typeof id === "number");
    return new Set(ids);
  }, [studentsWithPhoto]);

  const uploadedPhotos = React.useMemo(
    () =>
      state.uploaded_student_real_photos.filter(
        photo =>
          photo.id === undefined || !assignedUploadedPhotoIds.has(photo.id),
      ),
    [assignedUploadedPhotoIds, state.uploaded_student_real_photos],
  );

  const handleChangeClassId = (id: number) => {
    setSelectedClassId(id);
  };
  const handleUpload = (files: File[]) => {
    dispatch(peep(uploadStudentRealPhotos({ files })));
  };
  const handleDrop = ({
    photo_id,
    student,
  }: {
    photo_id: number;
    student: StudentWithUploadedFile;
  }) => {
    const index = state.uploaded_student_real_photos.findIndex(
      photo => photo.id === photo_id,
    );
    if (index === -1) {
      return;
    }
    const droppedFile = state.uploaded_student_real_photos[index];
    setStudentsWithPhoto(prev => {
      const items = [...prev];
      const index = prev.findIndex(s => s.id === student.id);
      if (index === -1) {
        return items;
      }
      const newItem = { ...items[index], uploadedFile: { ...droppedFile } };
      items.splice(index, 1, newItem);
      return items;
    });
  };
  const handleCancelDrop = (student: StudentWithUploadedFile) => {
    const uploadedFile = student.uploadedFile;
    if (uploadedFile === undefined) {
      return;
    }
    setStudentsWithPhoto(prev => {
      const items = [...prev];
      const index = prev.findIndex(s => s.id === student.id);
      if (index === -1) {
        return items;
      }
      const newItem = { ...items[index], uploadedFile: undefined };
      items.splice(index, 1, newItem);
      return items;
    });
  };
  const handleDeletePhoto = (student: StudentWithUploadedFile) => {
    if (student.real_photo === undefined || student.real_photo === null) {
      return;
    }
    const current_real_photo_id = student.real_photo.id!;
    setStudentsWithPhoto(prev => {
      const items = [...prev];
      const index = prev.findIndex(s => s.id === student.id);
      if (index === -1) {
        return items;
      }
      const newItem = { ...items[index], real_photo: undefined };
      items.splice(index, 1, newItem);
      return items;
    });
    setDeletedRealPhotoIds(prev => [
      ...prev,
      { student_id: student.id, real_photo_id: current_real_photo_id },
    ]);
  };
  const handleSubmit = () => {
    const buildData = (
      students: StudentWithUploadedFile[],
    ): BulkUpsertStudentRealPhotosRequest => {
      return {
        ids: students.map(s => {
          const deletedId = deletedRealPhotoIds.find(
            item => item.student_id === s.id,
          );
          if (deletedId) {
            return {
              student_id: s.id,
              blob_id: null,
              delete_real_photo_id: deletedId.real_photo_id,
            };
          } else {
            return {
              student_id: s.id,
              blob_id: s.uploadedFile?.id || null,
            };
          }
        }),
      };
    };
    const params = buildData(studentsWithPhoto);
    dispatch(peep(bulkUpsertStudentRealPhotos(params)));
  };

  const deleteStudentRealPhotoUploadedFileFunc = React.useCallback(
    (id: number) => {
      dispatch(peep(deleteStudentRealPhotoUploadedFile(id)));
    },
    [dispatch, peep],
  );

  React.useEffect(() => {
    dispatch(fetchStudentRealPhotoUploadedFiles());
  }, [dispatch]);

  React.useEffect(() => {
    dispatch(fetchClasses(currentYear));
  }, [dispatch, currentYear]);

  React.useEffect(() => {
    if (selectedClassId !== 0) {
      dispatch(
        fetchStudents({
          class_id: `${selectedClassId}`,
          include_real_photo: true,
        }),
      );
    } else {
      dispatch(clearStudents());
    }
  }, [dispatch, selectedClassId]);

  React.useEffect(() => {
    if (state.success_message === FETCH_STUDENTS_SUCCESS_MESSAGE) {
      setStudentsWithPhoto([...state.students]);
    }
    if (
      state.success_message === BULK_UPSERT_STUDENT_REAL_PHOTOS_SUCCESS_MESSAGE
    ) {
      Toast.success(state.success_message);
      dispatch(
        fetchStudents({
          class_id: `${selectedClassId}`,
          include_real_photo: true,
        }),
      );
      dispatch({ types: CLEAR_STUDENT_REAL_PHOTOS });
    }
    if (state.success_message !== "") {
      dispatch(clearSuccessMessage());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.success_message, state.students]);

  React.useEffect(() => {
    if (
      state.error === UPLOAD_STUDENT_REAL_PHOTOS_FAILURE_MESSAGE ||
      state.error === DELETE_STUDENT_REAL_PHOTO_UPLOADED_FILE_FAILURE_MESSAGE ||
      state.error === BULK_UPSERT_STUDENT_REAL_PHOTOS_FAILURE_MESSAGE
    ) {
      Toast.error(state.error);
    }
  }, [state.error]);

  React.useEffect(() => {
    return () => dispatch(clearErrorMessage());
  }, [dispatch]);

  return (
    <UploadStudentRealPhotos
      calling={calling}
      students={studentsWithPhoto}
      classes={state.classes}
      selectedClassId={selectedClassId}
      uploadedPhotos={uploadedPhotos}
      onChangeClassId={handleChangeClassId}
      onUpload={handleUpload}
      onDrop={handleDrop}
      onCancelDrop={handleCancelDrop}
      onDeletePhoto={handleDeletePhoto}
      onSubmit={handleSubmit}
      deleteStudentRealPhotoUploadedFile={
        deleteStudentRealPhotoUploadedFileFunc
      }
    />
  );
}
